import React from 'react'

import { FormattedMessage } from 'react-intl'

import Theme from '@theme'
import { Header } from '@tribegroup'
import {
  CancelButton,
  CancelButtonWrapper,
  LoadMoreHint,
  RejectSelectedButton,
  SelectAllScopeBanner,
  SelectAllScopeButton,
  SelectAllScopeNotice,
  SelectedCount,
  SelectionActionButton,
  SelectionBarStyled,
  SelectionModeActions,
} from './VibeCheckSelectionMode.styled'

interface IProps {
  selectedCount: number
  // Whether unloaded pages exist beyond the loaded list (adversarial finding
  // 2): "Select all pending" only reaches the LOADED creators, and no
  // truthful pending-total exists in state — so the bar states the selected
  // count alone and shows a load-more hint instead of implying an "N of N"
  // complete selection. BRAND-9550 makes this the gate for the escalation
  // banner too: with no unloaded pages there is nothing to escalate TO.
  hasMore?: boolean
  onSelectAllPending: VoidFunction
  onRejectSelected: VoidFunction
  onCancel: VoidFunction
  // --- BRAND-9550 select-all-pending scope escalation ---
  // How many creators on the loaded pages are selectable at all, so the
  // banner can say "All N on this page" only when that is literally true.
  selectablePageCount?: number
  // True once the user escalated: the request carries a SCOPE FLAG rather
  // than an id list, so no literal count can honestly be shown and per-row
  // selection is locked.
  selectAllScopeActive?: boolean
  // Which run scope the escalation would cover, mirroring the viewed scope.
  runScopeLabel?: 'all' | 'run'
  // Absent when the parent cannot resolve which run the escalation would bind
  // to (BRAND-9550): the banner is then withheld entirely rather than offering
  // an action that would have to send an unbound scope.
  onSelectAllScope?: VoidFunction
}

// BRAND-9514: title-bar strip shown while the vibe-check list is in bulk
// selection mode — actions left, count centered, cancel right, mirroring the
// Inbox TitleBar SelectionMode. Selection state lives in the parent
// (AgentSourcedCreatorListView); this component is purely presentational.
const VibeCheckSelectionMode: React.FC<IProps> = ({
  selectedCount,
  hasMore = false,
  onSelectAllPending,
  onRejectSelected,
  onCancel,
  selectablePageCount = 0,
  selectAllScopeActive = false,
  runScopeLabel = 'run',
  onSelectAllScope,
}) => {
  const hasSelection = selectAllScopeActive || selectedCount > 0
  // BRAND-9550. Gmail-style hybrid: page-select stays literal, and the offer
  // to escalate appears only where it can be honest — there must be unloaded
  // pages to reach (hasMore) and something already selected to escalate FROM.
  // A single-page run never shows it: everything is already literally
  // selected there, so "select all" would be theatre.
  //
  // `onSelectAllScope` is part of the gate, not just plumbing: the parent
  // withholds it when it cannot resolve which run the escalation would bind to
  // (BRAND-9550), and a banner whose only button does nothing is worse than no
  // banner. Page-select is unaffected either way.
  const canEscalate =
    !selectAllScopeActive && hasMore && selectedCount > 0 && Boolean(onSelectAllScope)

  return (
    <>
      <SelectionBarStyled alignCenter justifyBetween data-testid="vibe-check-selection-bar">
        <SelectionModeActions alignCenter justifyStart>
          <SelectionActionButton
            type="button"
            onClick={onSelectAllPending}
            disabled={selectAllScopeActive}
            data-testid="selection-select-all-pending"
          >
            <FormattedMessage id="creatorSearch.vibeCheck.selection.selectAllPending" />
          </SelectionActionButton>
          <RejectSelectedButton
            type="button"
            onClick={onRejectSelected}
            disabled={!hasSelection}
            data-testid="selection-reject-selected"
          >
            <FormattedMessage id="creatorSearch.vibeCheck.selection.rejectSelected" />
          </RejectSelectedButton>
        </SelectionModeActions>
        <SelectedCount hilightCount={hasSelection} data-testid="selection-count">
          <Header color={Theme.grey700Color}>
            {selectAllScopeActive ? (
              // The literal count belongs to the loaded page only — under a
              // scope flag it would understate the real set, which is the
              // exact lie BRAND-9550 exists to remove.
              <span className="count">
                <FormattedMessage id="creatorSearch.vibeCheck.selection.count.allPending" />
              </span>
            ) : (
              <FormattedMessage
                id="creatorSearch.vibeCheck.selection.count"
                values={{
                  count: selectedCount,
                  span: (chunks) => <span className="count">{chunks}</span>,
                }}
              />
            )}
          </Header>
        </SelectedCount>
        <CancelButtonWrapper>
          <CancelButton type="button" onClick={onCancel} data-testid="selection-cancel">
            <FormattedMessage id="core.text.cancel" />
          </CancelButton>
        </CancelButtonWrapper>
      </SelectionBarStyled>
      {canEscalate && (
        <SelectAllScopeBanner data-testid="selection-select-all-scope-banner">
          <FormattedMessage
            id="creatorSearch.vibeCheck.selection.selectAllScope.pageSelected"
            values={{
              count: selectedCount,
              // ICU `select` takes a string; only claim "All N" when the whole
              // loaded page really is selected.
              allOnPage: String(selectablePageCount > 0 && selectedCount >= selectablePageCount),
            }}
          />{' '}
          <SelectAllScopeButton
            type="button"
            onClick={onSelectAllScope}
            data-testid="selection-select-all-scope-action"
          >
            <FormattedMessage
              id="creatorSearch.vibeCheck.selection.selectAllScope.action"
              values={{ scope: runScopeLabel }}
            />
          </SelectAllScopeButton>
        </SelectAllScopeBanner>
      )}
      {selectAllScopeActive && (
        <SelectAllScopeNotice data-testid="selection-select-all-scope-active">
          <FormattedMessage
            id="creatorSearch.vibeCheck.selection.selectAllScope.active"
            values={{ scope: runScopeLabel }}
          />
        </SelectAllScopeNotice>
      )}
      {/* Under a scope flag, scrolling for more pages no longer changes the
          selection at all — the hint would be actively misleading. */}
      {hasMore && !selectAllScopeActive && (
        <LoadMoreHint data-testid="selection-load-more-hint">
          <FormattedMessage id="creatorSearch.vibeCheck.selection.loadMoreHint" />
        </LoadMoreHint>
      )}
    </>
  )
}

export { VibeCheckSelectionMode }
export default VibeCheckSelectionMode
